const UUID_PATTERN =
  /\b[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\b/gi;
const LONG_HEX_ID_PATTERN = /\b[a-f0-9]{24,}\b/gi;
const UUID_TEST_PATTERN =
  /\b[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\b/i;
const LONG_HEX_ID_TEST_PATTERN = /\b[a-f0-9]{24,}\b/i;
const TOOL_COLUMNS_SUFFIX_PATTERN = /\s*->\s*columns:\s*\[[^\]]*]\s*$/i;
const INTERNAL_FUNCTION_PATTERN = /[a-zA-Z_][a-zA-Z0-9_]*\s*\(/;
const SNAKE_CASE_TOKEN_PATTERN = /\b[a-z]+_[a-z0-9_]+\b/;

export interface ParsedToolInvocation {
  toolName: string;
  args: Record<string, string>;
}

function stripWrappingQuotes(value: string): string {
  const trimmed = value.trim();
  if (
    (trimmed.startsWith("'") && trimmed.endsWith("'")) ||
    (trimmed.startsWith('"') && trimmed.endsWith('"'))
  ) {
    return trimmed.slice(1, -1).trim();
  }
  return trimmed;
}

export function removeInternalArtifacts(rawDescription?: string): string {
  if (!rawDescription) return "";

  let cleaned = rawDescription;
  cleaned = cleaned.replace(TOOL_COLUMNS_SUFFIX_PATTERN, "");
  cleaned = cleaned.replace(UUID_PATTERN, "");
  cleaned = cleaned.replace(LONG_HEX_ID_PATTERN, "");
  cleaned = cleaned.replace(/\bdata[_\s-]?id\b\s*[:=]?\s*[a-zA-Z0-9_-]{6,}/gi, "");
  cleaned = cleaned.replace(/\bfrom\s*$/i, "");
  cleaned = cleaned.replace(/\(\s*\)/g, "");
  cleaned = cleaned.replace(/\s{2,}/g, " ");
  cleaned = cleaned.replace(/\s+,/g, ",");
  cleaned = cleaned.trim();
  cleaned = cleaned.replace(/^[-:,\s]+|[-:,\s]+$/g, "");
  return cleaned;
}

export function parseToolInvocation(rawDescription?: string): ParsedToolInvocation | null {
  const cleaned = removeInternalArtifacts(rawDescription);
  if (!cleaned) return null;

  const toolCallMatch = cleaned.match(/^\s*([a-zA-Z_][a-zA-Z0-9_]*)\((.*)\)\s*$/);
  if (!toolCallMatch) return null;

  const toolName = toolCallMatch[1];
  const argsStr = toolCallMatch[2].trim();
  const args: Record<string, string> = {};

  if (argsStr.length > 0) {
    const argPattern = /([a-zA-Z_][a-zA-Z0-9_]*)\s*=\s*([^,)]*)/g;
    let match = argPattern.exec(argsStr);
    while (match) {
      const key = match[1];
      const value = stripWrappingQuotes(match[2]);
      if (value) {
        args[key] = value;
      }
      match = argPattern.exec(argsStr);
    }
  }

  return { toolName, args };
}

export function toPlainText(rawDescription?: string): string {
  const cleaned = removeInternalArtifacts(rawDescription);
  if (!cleaned) return "";

  let plain = cleaned;
  plain = plain.replace(/_/g, " ");
  plain = plain.replace(/\s{2,}/g, " ");
  plain = plain.replace(/^[-:,\s]+|[-:,\s]+$/g, "");
  return plain.trim();
}

export function hasInternalTokens(value: string): boolean {
  if (!value) return true;

  if (INTERNAL_FUNCTION_PATTERN.test(value)) return true;
  if (UUID_TEST_PATTERN.test(value)) return true;
  if (LONG_HEX_ID_TEST_PATTERN.test(value)) return true;
  if (/\bdata[_\s-]?id\b/i.test(value)) return true;
  if (SNAKE_CASE_TOKEN_PATTERN.test(value)) return true;

  return false;
}

export function looksLikeOpaqueIdentifier(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) return true;

  if (UUID_TEST_PATTERN.test(trimmed)) return true;
  if (LONG_HEX_ID_TEST_PATTERN.test(trimmed)) return true;
  if (/^[a-zA-Z0-9_-]{16,}$/.test(trimmed)) return true;

  return false;
}

export function finalizeLabelText(value: string): string {
  let label = value.trim();
  label = label.replace(/\s{2,}/g, " ");
  label = label.replace(/\bfrom\s*$/i, "");
  label = label.replace(/^[-:,\s]+|[-:,\s]+$/g, "");
  if (!label) return "";

  return label.charAt(0).toUpperCase() + label.slice(1);
}
